"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import BrandSignature3D from "@/components/branding/BrandSignature3D";
import type { Locale } from "@/config/i18n";
import { tr } from "@/config/localeText";
import { CHAUFFEUR_HUB } from "./hubDict";
import imageService from "@/assets/images/chauffeur/services/Experience-passager.png";
import imageAeroport from "@/assets/images/chauffeur/aeroport/Transferts-aeroports-heliports.png";

type MotionExperienceSectionProps = { locale?: Locale };

export default function MotionExperienceSection({
  locale = "fr",
}: MotionExperienceSectionProps): JSX.Element {
  const d = tr(CHAUFFEUR_HUB, locale).motion;
  return (
    <section className="bg-primary py-14 text-black">
      <div className="mx-auto grid max-w-[90rem] gap-6 px-6 md:px-8 lg:grid-cols-[1fr_0.95fr]">
        <motion.article
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.45 }}
          className="rounded-3xl border border-black/20 bg-white/85 p-6"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-black/60">
            {d.eyebrow}
          </p>
          <h2 className="mt-2 font-space-age text-xl tracking-[0.2em]">
            {d.heading}
          </h2>
          <p className="mt-3 max-w-2xl text-sm text-black/75">{d.text}</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <div className="relative aspect-[16/10] overflow-hidden rounded-xl border border-black/10">
              <Image
                src={imageService}
                alt={d.leftAlt}
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, 30vw"
              />
            </div>
            <div className="relative aspect-[16/10] overflow-hidden rounded-xl border border-black/10">
              <Image
                src={imageAeroport}
                alt={d.rightAlt}
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, 30vw"
              />
            </div>
          </div>
        </motion.article>

        <motion.div
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.5 }}
          className="relative min-h-[260px] overflow-hidden rounded-3xl border border-black/20 bg-black"
        >
          <BrandSignature3D accentColor="#f3cf86" />
          <div className="pointer-events-none absolute inset-0 bg-black/55" />
          <div className="absolute inset-x-0 bottom-0 z-10 p-5 text-white">
            <p className="text-xs uppercase tracking-[0.18em] text-gold-200/85">
              {d.badge}
            </p>
            <p className="mt-1 text-sm text-white/85">{d.signatureText}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
